import { useEffect, useState } from 'react';
import { accountsApi, type Account } from '../api/client.js';
import { SkeletonRows, TableEmpty, ErrorBanner } from '../components/States.js';
import { roleCan, permHint } from '../utils/permissions.js';

const TYPE_LABELS: Record<string, string> = {
  asset: '资产',
  liability: '负债',
  equity: '权益',
  revenue: '收入',
  expense: '费用',
};

const TYPE_COLORS: Record<string, string> = {
  asset: 'bg-blue-100 text-blue-700',
  liability: 'bg-red-100 text-red-700',
  equity: 'bg-purple-100 text-purple-700',
  revenue: 'bg-green-100 text-green-700',
  expense: 'bg-orange-100 text-orange-700',
};

export default function Accounts() {
  const canWrite = roleCan('account:write');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);

  // 表单状态
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [type, setType] = useState('asset');
  const [direction, setDirection] = useState<'debit' | 'credit'>('debit');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await accountsApi.list({ pageSize: 100 });
      setAccounts(res.accounts);
      setTotal(res.total);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    try {
      await accountsApi.create({ code, name, type, direction });
      setCode('');
      setName('');
      setShowForm(false);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const remove = async (acct: Account) => {
    if (!window.confirm(`确认删除科目「${acct.name}(${acct.code})」？`)) return;
    try {
      await accountsApi.remove(acct.id);
      await load();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-bold text-slate-800">会计科目</h2>
        <button
          onClick={() => setShowForm(!showForm)}
          disabled={!canWrite && !showForm}
          title={permHint('account:write')}
          className="btn btn-primary"
        >
          {showForm ? '取消' : '+ 新增科目'}
        </button>
      </div>

      {error && (
        <div className="mb-4">
          <ErrorBanner message={error} onRetry={error.startsWith('加载') ? load : undefined} />
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mb-6 p-4 bg-white rounded-xl shadow-sm grid grid-cols-5 gap-3 items-end">
          <div>
            <label className="block text-xs text-slate-500 mb-1">科目编码 *</label>
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="如 1001"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">科目名称 *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="如 库存现金"
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              required
            />
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">类型</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              {Object.entries(TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">记账方向</label>
            <select
              value={direction}
              onChange={(e) => setDirection(e.target.value as 'debit' | 'credit')}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
            >
              <option value="debit">借方</option>
              <option value="credit">贷方</option>
            </select>
          </div>
          <button type="submit" disabled={!canWrite} title={permHint('account:write')} className="btn btn-accent">
            保存
          </button>
        </form>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="text-left px-4 py-3">编码</th>
              <th className="text-left px-4 py-3">名称</th>
              <th className="text-left px-4 py-3">类型</th>
              <th className="text-left px-4 py-3">方向</th>
              <th className="text-left px-4 py-3">状态</th>
              <th className="text-right px-4 py-3">操作</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <SkeletonRows colSpan={6} />
            ) : accounts.length === 0 ? (
              <TableEmpty
                colSpan={6}
                icon="🧾"
                title="还没有科目"
                hint="点击右上角「+ 新增科目」，先搭好科目体系再开始记账"
              />
            ) : (
              accounts.map((acct) => (
                <tr key={acct.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono">{acct.code}</td>
                  <td className="px-4 py-3">{acct.name}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs ${TYPE_COLORS[acct.type] || ''}`}>
                      {TYPE_LABELS[acct.type] || acct.type}
                    </span>
                  </td>
                  <td className="px-4 py-3">{acct.direction === 'debit' ? '借' : '贷'}</td>
                  <td className="px-4 py-3">
                    <span className={acct.isActive ? 'text-green-600' : 'text-slate-400'}>
                      {acct.isActive ? '启用' : '停用'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => remove(acct)}
                      disabled={!canWrite}
                      title={permHint('account:write')}
                      className="text-red-500 hover:text-red-700 text-xs disabled:text-slate-300 disabled:cursor-not-allowed"
                    >
                      删除
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        <div className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100">
          共 {total} 个科目
        </div>
      </div>
    </div>
  );
}
